import { Button, Flex, Input, InputGroup, InputLeftElement, TableContainer, Tooltip, IconButton } from "@chakra-ui/react";
import { IconEdit, IconPlus, IconUpload } from "@tabler/icons-react";
import { createColumnHelper } from "@tanstack/react-table";
import { DataTable } from "am-tax-fe-core";
import { ChangeEvent, FunctionComponent, useMemo, useState } from "react";
import { LuSearch } from "react-icons/lu";
import { TableRowProps } from "../../../utils/types";
import { Entity } from "../../../api/entities/entityApi";
import { EntityModal } from "./EntityModal";
import { SectionHeader } from "../../../components/SectionHeader";
import { useGetEntitiesByClientId } from "../../../api/entities/entityQueries";
import { useGetJurisdictions } from "../../../api/jurisdictions/jurisdictionQueries";
import { EntityBulkUploadModal } from "./EntityBulkUploadModal";

interface Props {
    clientId?: string;
    entityId?: string;
}

export const EntityPanel: FunctionComponent<Props> = ({ clientId, entityId }) => {
    const [showCreateOrEditEntityDialog, setShowCreateOrEditEntityDialog] = useState(false);
    const [showBulkUpload, setShowBulkUpload] = useState(false);
    const [entityData, setEntityData] = useState<Entity | undefined>();

    const columnHelper = createColumnHelper<Entity>();
    const { data: entities = [], isLoading, refetch } = useGetEntitiesByClientId(clientId);
    const { data: jurisdictions = [] } = useGetJurisdictions();

    const [searchTerm, setSearchTerm] = useState<string>("");

    const defaultEntityName = useMemo(() => {
        return entityId ? entities.find((e: Entity) => e.id === entityId)?.fullLegalName || "" : "";
    }, [entityId, entities]);

    const filteredData = useMemo(() => {
        let filtered = entities;

        if (entityId) {
            filtered = filtered.filter((entity: Entity) => entity.id === entityId);
        }

        if (searchTerm.trim() !== "") {
            const value = searchTerm.trim().toLowerCase();
            filtered = filtered.filter(
                (c: Entity) =>
                    c.fullLegalName?.toLowerCase().includes(value) ||
                    c.abbreviatedName?.toLowerCase().includes(value) ||
                    c.operatingJurisdiction?.toLowerCase().includes(value) ||
                    c.description?.toLowerCase().includes(value) ||
                    c.headcount?.toString().toLowerCase().includes(value) ||
                    c.characterization?.toLowerCase().includes(value) ||
                    (c.operatingJurisdiction &&
                        jurisdictions
                            .find(j => j.id === c.operatingJurisdiction)
                            ?.name?.toLowerCase()
                            .includes(value)),
            );
        }

        return filtered;
    }, [entities, entityId, searchTerm, jurisdictions]);

    function enableEdit(entity: Entity) {
        setEntityData(entity);
        setShowCreateOrEditEntityDialog(true);
    }

    function closeCreateOrEditEntityDialog() {
        setEntityData(undefined);
        setShowBulkUpload(false);
        setShowCreateOrEditEntityDialog(false);
        refetch();
    }

    function closeBulkUploadDialog() {
        setShowBulkUpload(false);
        refetch();
    }

    function handleSave() {
        refetch();
    }

    function handleFilter(event: ChangeEvent<HTMLInputElement>) {
        const value = event.target.value;
        setSearchTerm(value);
    }

    return (
        <Flex flexFlow="column nowrap">
            <SectionHeader title="Entities">
                <InputGroup flex="0 1 35rem">
                    <InputLeftElement pointerEvents="none">
                        <LuSearch />
                    </InputLeftElement>

                    <Input defaultValue={defaultEntityName} placeholder="Search Entities" onChange={handleFilter} />
                </InputGroup>
                <Flex gap="0.5rem">
                    <Button leftIcon={<IconUpload />} variant="outline" flexShrink="0" onClick={() => setShowBulkUpload(true)}>
                        Bulk Upload from Excel
                    </Button>
                    <Button leftIcon={<IconPlus />} variant="primary" flexShrink="0" onClick={() => setShowCreateOrEditEntityDialog(true)}>
                        Add Entity
                    </Button>
                </Flex>
            </SectionHeader>
            <TableContainer overflowY="auto" sx={{ mt: "1rem", backgroundColor: "chakra-body-bg", maxHeight: "70vh" }}>
                <DataTable
                    enableFilters={false}
                    tableProps={{ size: "sm" }}
                    isLoading={isLoading}
                    noData={isLoading ? undefined : "No entities found"}
                    data={filteredData}
                    defaultColumn={{ meta: { tableColumnHeaderProps: { pt: "1rem", zIndex: 1 } } }}
                    columns={[
                        columnHelper.accessor("fullLegalName", {
                            header: "Legal Name",
                            cell: (props: TableRowProps<Entity>) => props.getValue(),
                        }),
                        columnHelper.accessor("abbreviatedName", {
                            header: "Abbreviated Name",
                            cell: (props: TableRowProps<Entity>) => props.getValue(),
                        }),
                        columnHelper.accessor("operatingJurisdiction", {
                            header: "Operating Jurisdiction",
                            cell: (props: TableRowProps<Entity>) => jurisdictions.find(j => j.id === props.getValue())?.name,
                        }),
                        // TODO: Ensure the Entity type and backend API include the 'characterization' property.
                        columnHelper.accessor("characterization", {
                            header: "Characterization",
                            cell: (props: TableRowProps<Entity>) => {
                                const value = props.getValue()?.toString().split(",").join(", ") || "N/A";
                                return value;
                            },
                        }),
                        {
                            id: "Edit Entity",
                            cell: props => {
                                const row = props.row.original;
                                return (
                                    <Tooltip label="Edit Entity">
                                        <IconButton
                                            icon={<IconEdit />}
                                            aria-label="Edit Entity"
                                            variant="outline"
                                            onClick={evt => {
                                                evt.stopPropagation();
                                                enableEdit(row);
                                            }}
                                        />
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                />
            </TableContainer>
            <Flex>
                <EntityModal
                    show={showCreateOrEditEntityDialog}
                    onClose={closeCreateOrEditEntityDialog}
                    rowData={entityData}
                    clientId={clientId || ""}
                    jurisdictions={jurisdictions}
                    onSave={handleSave}
                />
            </Flex>
            <Flex>
                <EntityBulkUploadModal show={showBulkUpload} onClose={closeBulkUploadDialog} clientId={clientId || ""} onSave={handleSave} />
            </Flex>
        </Flex>
    );
};
